/**
 * FormBuilderLibrary - Left panel of the form builder.
 *
 * Displays draggable field type items grouped by collapsible categories.
 * Uses react-dnd drag source with type 'FIELD_TYPE'.
 */

import { cn } from '@/shared/lib/cn';
import { Badge, Input } from '@/shared/ui';
import {
  AlignLeft,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronDownSquare,
  ChevronRight,
  CircleDot,
  Clock,
  GitBranch,
  GripVertical,
  Hash,
  Image,
  Layers,
  Mail,
  PanelLeftClose,
  PanelLeftOpen,
  PenTool,
  Phone,
  Search,
  Tag,
  ToggleLeft,
  Type,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import type { FieldTemplate } from './types';

const DND_TYPE_FIELD = 'FIELD_TYPE';

const FIELD_ICON_MAP: Record<string, React.ReactNode> = {
  text: <Type className="w-4 h-4" />,
  textarea: <AlignLeft className="w-4 h-4" />,
  number: <Hash className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  tel: <Phone className="w-4 h-4" />,
  date: <Calendar className="w-4 h-4" />,
  time: <Clock className="w-4 h-4" />,
  radio: <CircleDot className="w-4 h-4" />,
  checkbox: <CheckSquare className="w-4 h-4" />,
  option: <ChevronDownSquare className="w-4 h-4" />,
  switch: <ToggleLeft className="w-4 h-4" />,
  s3_image: <Image className="w-4 h-4" />,
  signature: <PenTool className="w-4 h-4" />,
  label: <Tag className="w-4 h-4" />,
  group_controls: <Layers className="w-4 h-4" />,
  conditional_field: <GitBranch className="w-4 h-4" />,
};

const CATEGORIES = [
  {
    name: 'Inputs',
    items: [
      { type: 'text', label: 'Text', category: 'Inputs' },
      { type: 'textarea', label: 'Textarea', category: 'Inputs' },
      { type: 'number', label: 'Number', category: 'Inputs' },
      { type: 'email', label: 'Email', category: 'Inputs' },
      // CWL-132: a phone question must be authored as `tel`, not `text` — mobile
      // keys its UK mobile validation (and the phone keypad) off this control type.
      { type: 'tel', label: 'Phone', category: 'Inputs' },
      { type: 'date', label: 'Date', category: 'Inputs' },
      { type: 'time', label: 'Time', category: 'Inputs' },
    ],
  },
  {
    name: 'Choice',
    items: [
      { type: 'radio', label: 'Radio', category: 'Choice' },
      { type: 'checkbox', label: 'Checkbox', category: 'Choice' },
      { type: 'option', label: 'Dropdown', category: 'Choice' },
      { type: 'switch', label: 'Switch', category: 'Choice' },
    ],
  },
  {
    name: 'Media',
    items: [
      { type: 's3_image', label: 'Image Upload', category: 'Media' },
      { type: 'signature', label: 'Signature', category: 'Media' },
    ],
  },
  {
    name: 'Layout',
    items: [
      { type: 'label', label: 'Label', category: 'Layout' },
      { type: 'group_controls', label: 'Group', category: 'Layout' },
    ],
  },
] as const;

interface DraggableItemProps {
  template: FieldTemplate;
  onAdd: () => void;
}

function DraggableItem({ template, onAdd }: DraggableItemProps) {
  const ref = useRef<HTMLButtonElement>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DND_TYPE_FIELD,
    item: { template },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  drag(ref);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onAdd}
      className={cn(
        'flex items-center gap-2 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-border dark:bg-card',
        'text-left text-sm hover:border-[#E84A2B] hover:shadow-sm transition-all cursor-grab dark:hover:border-[#E84A2B]',
        isDragging && 'opacity-40',
      )}
    >
      <GripVertical className="w-3.5 h-3.5 text-zinc-400 dark:text-muted-foreground flex-shrink-0" />
      <span className="text-zinc-500 dark:text-muted-foreground flex-shrink-0">
        {FIELD_ICON_MAP[template.type] ?? <Type className="w-4 h-4" />}
      </span>
      <span className="font-medium text-zinc-700 dark:text-foreground truncate">
        {template.label}
      </span>
    </button>
  );
}

interface FormBuilderLibraryProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onAddField: (template: FieldTemplate) => void;
  configTemplates?: FieldTemplate[];
}

export default function FormBuilderLibrary({
  collapsed,
  onToggleCollapse,
  onAddField,
  configTemplates,
}: FormBuilderLibraryProps) {
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    Inputs: true,
    Choice: true,
    Media: true,
    Layout: true,
  });

  const categories = useMemo(() => {
    if (configTemplates && configTemplates.length > 0) {
      const grouped: Record<string, FieldTemplate[]> = {};
      for (const t of configTemplates) {
        const cat = t.category || 'Other';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(t);
      }
      return Object.entries(grouped).map(([name, items]) => ({ name, items }));
    }
    return CATEGORIES.map((c) => ({
      name: c.name,
      items: c.items.map((item) => ({ ...item }) as FieldTemplate),
    }));
  }, [configTemplates]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) => item.label.toLowerCase().includes(q) || item.type.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [categories, search]);

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  if (collapsed) {
    return (
      <div className="flex flex-col items-center py-4 border rounded-lg bg-white dark:bg-card h-full">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-accent transition-colors"
          title="Expand field library"
        >
          <PanelLeftOpen className="w-4 h-4 text-zinc-500 dark:text-muted-foreground" />
        </button>
        <span
          className="mt-4 text-xs font-semibold text-zinc-500 dark:text-muted-foreground"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Fields
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col border rounded-lg bg-white dark:bg-card h-full">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Field Library
          </h3>
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">Drag or click to add</p>
        </div>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-accent transition-colors"
          title="Collapse field library"
        >
          <PanelLeftClose className="w-4 h-4 text-zinc-500 dark:text-muted-foreground" />
        </button>
      </div>

      <div className="px-4 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400 dark:text-muted-foreground" />
          <Input
            placeholder="Search fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {filteredCategories.length === 0 && (
          <p className="text-xs text-zinc-400 dark:text-muted-foreground text-center py-4">
            No fields match your search.
          </p>
        )}

        {filteredCategories.map((cat) => {
          const isExpanded = expandedCategories[cat.name] !== false;
          return (
            <div key={cat.name}>
              <button
                type="button"
                onClick={() => toggleCategory(cat.name)}
                className="flex items-center gap-1 w-full text-left py-1"
              >
                {isExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5 text-zinc-400 dark:text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-400 dark:text-muted-foreground" />
                )}
                <span className="text-xs font-semibold text-zinc-600 dark:text-muted-foreground uppercase tracking-wide">
                  {cat.name}
                </span>
                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                  {cat.items.length}
                </Badge>
              </button>

              {isExpanded && (
                <div className="space-y-1 mt-1">
                  {cat.items.map((item) => (
                    <DraggableItem key={item.type} template={item} onAdd={() => onAddField(item)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
